import { useEffect, useId, useRef, useState } from "react";

import { TeacherPlannedLessonsText } from "./TeacherPlannedLessons";

interface AssistantOption {
  id: string;
  name: string;
  workloadLabel?: string;
  planned_lessons?: number | null;
}

export function AssistantMultiSelect({
  value,
  options,
  onChange,
  fixedIds = [],
  disabledIds = []
}: {
  value: string[];
  options: AssistantOption[];
  onChange: (value: string[]) => void;
  fixedIds?: string[];
  disabledIds?: string[];
}): React.JSX.Element {
  const popupId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const atLimit = value.length >= 3;
  const selectedNames = options
    .filter((option) => value.includes(option.id))
    .map((option) => option.name);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleOptions = normalizedQuery
    ? options.filter((option) =>
        [option.name, option.workloadLabel]
          .filter(Boolean)
          .some((text) => text?.toLocaleLowerCase().includes(normalizedQuery))
      )
    : options;

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: PointerEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  const toggle = (id: string, checked: boolean) => {
    if (!checked) {
      if (!fixedIds.includes(id)) onChange(value.filter((item) => item !== id));
      return;
    }
    if (!value.includes(id) && !atLimit) onChange([...value, id]);
  };

  function close(): void {
    setOpen(false);
    setQuery("");
    triggerRef.current?.focus();
  }

  return (
    <fieldset className="assistant-multi-select">
      <legend>助教老师</legend>
      <div
        className="multi-select-control"
        ref={rootRef}
        onKeyDown={(event) => {
          if (!open || event.key !== "Escape") return;
          event.preventDefault();
          event.stopPropagation();
          close();
        }}
      >
        <button
          ref={triggerRef}
          className="multi-select-trigger"
          type="button"
          aria-label="助教老师"
          aria-expanded={open}
          aria-controls={popupId}
          onClick={() => {
            if (open) setQuery("");
            setOpen(!open);
          }}
        >
          <span className={selectedNames.length ? "" : "placeholder"}>
            {selectedNames.length ? selectedNames.join("、") : "请选择（可多选）"}
          </span>
          <small>已选 {value.length}/3</small>
          <span className="multi-select-chevron" aria-hidden="true">⌄</span>
        </button>
        {open ? (
          <div id={popupId} className="multi-select-popover">
            <div className="multi-select-search">
              <input
                type="search"
                value={query}
                aria-label="搜索助教老师"
                placeholder="输入教师姓名"
                autoFocus
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
            <div className="multi-select-toolbar">
              <button
                className="text-button"
                type="button"
                disabled={value.every((id) => fixedIds.includes(id))}
                onClick={() => onChange(value.filter((id) => fixedIds.includes(id)))}
              >
                清空
              </button>
            </div>
            <div className="multi-select-options">
              {visibleOptions.length ? visibleOptions.map((option) => {
                const checked = value.includes(option.id);
                const fixed = fixedIds.includes(option.id);
                const disabled = disabledIds.includes(option.id);
                return (
                  <label className="check-row" key={option.id}>
                    <input
                      checked={checked}
                      disabled={fixed || (disabled && !checked) || (!checked && atLimit)}
                      onChange={(event) => toggle(option.id, event.target.checked)}
                      type="checkbox"
                    />
                    <span className="teacher-option-label">
                      <strong className="teacher-option-name">
                        {option.name}{fixed ? "（必须保留）" : ""}
                      </strong>
                      <span className="teacher-option-workload">
                        {option.workloadLabel ?? "总计 0节（主教0节，助教0节）"}
                      </span>
                      <TeacherPlannedLessonsText value={option.planned_lessons} />
                    </span>
                  </label>
                );
              }) : <p className="multi-select-empty">{options.length ? "没有匹配的教师" : "暂无可选教师"}</p>}
            </div>
            <div className="multi-select-footer">
              <span>{atLimit ? "最多选择 3 位助教" : `已选 ${value.length}/3 位`}</span>
              <button className="secondary-button" type="button" onClick={close}>完成</button>
            </div>
          </div>
        ) : null}
      </div>
    </fieldset>
  );
}
